import { Button, Flex, Table, Tag, Typography } from "antd"
import type { ColumnsType, SorterResult } from "antd/es/table/interface"
import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { StatusTag } from "./StatusTag"
import { formatDays, formatMoney, formatPercent, formatScore } from "./format"
import type { PerformanceMemberRow, PerformanceScore } from "./types"
import type { PerformanceSort } from "./use-performance-url-state"

const MIN_SAMPLE = 3
const PAGE_SIZE = 20

type SortValue = (row: PerformanceMemberRow) => number | string | null

const scoreValue = (score: PerformanceScore) =>
  score.available ? score.value : null

const SORT_VALUES: Record<string, SortValue> = {
  name: (r) => r.name.toLowerCase(),
  projects: (r) => r.projects,
  schedule: (r) => scoreValue(r.schedule_score),
  on_time: (r) => r.summary.on_time_percent,
  avg_delay: (r) => r.summary.avg_delay_days,
  financial: (r) => scoreValue(r.financial_score)
}

// Server rows are authoritative; sorting only reorders them. An unavailable
// value sorts last whichever way the column is sorted (spec §4 dashboard).
const sortRows = (rows: PerformanceMemberRow[], sort?: PerformanceSort) => {
  const value = sort && SORT_VALUES[sort.key]
  if (!sort || !value) return rows
  const sign = sort.dir === "ascend" ? 1 : -1
  return [...rows].sort((a, b) => {
    const va = value(a)
    const vb = value(b)
    if (va === null && vb === null) return 0
    if (va === null) return 1
    if (vb === null) return -1
    return va < vb ? -sign : va > vb ? sign : 0
  })
}

export type MemberDrill = "schedule_exceptions" | "financial_exceptions"

interface Props {
  rows: PerformanceMemberRow[]
  amountsVisible: boolean
  onOpenMember?: (row: PerformanceMemberRow, drill?: MemberDrill) => void
  sort?: PerformanceSort
  onSortChange?: (sort: PerformanceSort | null) => void
}

export function PerformanceMembersTable({
  rows,
  amountsVisible,
  sort,
  onSortChange,
  onOpenMember
}: Props) {
  const { t } = useTranslation()
  const [localSort, setLocalSort] = useState<PerformanceSort | undefined>()
  const activeSort = onSortChange ? sort : localSort

  const scoreCell = (
    score: PerformanceScore,
    kind: "schedule" | "financial"
  ) =>
    score.available ? (
      formatScore(score)
    ) : (
      <>
        <span aria-hidden="true">{formatScore(score)}</span>
        <span className="sr-only">
          {t("teamPerformance.table.scoreUnavailable", {
            kind: t(`teamPerformance.table.${kind}`),
            count: score.sample_size,
            min: MIN_SAMPLE
          })}
        </span>
      </>
    )

  const drillCell = (
    row: PerformanceMemberRow,
    drill: MemberDrill,
    content: React.ReactNode
  ) =>
    onOpenMember ? (
      <Button
        type="link"
        size="small"
        className="h-auto p-0"
        aria-label={t(
          drill === "schedule_exceptions"
            ? "teamPerformance.dashboard.drillSchedule"
            : "teamPerformance.dashboard.drillFinancial",
          {
            name: row.name,
            score: formatScore(
              drill === "schedule_exceptions"
                ? row.schedule_score
                : row.financial_score
            )
          }
        )}
        onClick={() => onOpenMember(row, drill)}
      >
        {content}
      </Button>
    ) : (
      content
    )

  const sorted = (key: string) => ({
    sorter: true,
    sortOrder: activeSort?.key === key ? activeSort.dir : null
  })

  const columns: ColumnsType<PerformanceMemberRow> = useMemo(
    () => [
      {
        key: "name",
        title: t("teamPerformance.table.member"),
        fixed: "left",
        width: 200,
        ...sorted("name"),
        render: (_, row) => (
          <Flex gap={6} align="center" wrap>
            {onOpenMember ? (
              <Button
                type="link"
                className="h-auto p-0"
                aria-label={t("teamPerformance.dashboard.openMember", {
                  name: row.name
                })}
                onClick={() => onOpenMember(row)}
              >
                <Typography.Text
                  strong
                  data-testid="member-name"
                  className="text-inherit"
                >
                  {row.name}
                </Typography.Text>
              </Button>
            ) : (
              <Typography.Text strong data-testid="member-name">
                {row.name}
              </Typography.Text>
            )}
            {!row.is_active && <Tag>{t("teamPerformance.table.inactive")}</Tag>}
          </Flex>
        )
      },
      {
        key: "role",
        title: t("teamPerformance.table.role"),
        render: (_, row) => t(`teamPerformance.roles.${row.role}`)
      },
      {
        key: "projects",
        title: t("teamPerformance.table.projects"),
        align: "right",
        ...sorted("projects"),
        render: (_, row) => row.projects
      },
      {
        key: "schedule",
        title: t("teamPerformance.table.schedule"),
        align: "right",
        ...sorted("schedule"),
        render: (_, row) =>
          drillCell(
            row,
            "schedule_exceptions",
            scoreCell(row.schedule_score, "schedule")
          )
      },
      {
        key: "on_time",
        title: t("teamPerformance.table.onTime"),
        align: "right",
        ...sorted("on_time"),
        render: (_, row) => formatPercent(row.summary.on_time_percent)
      },
      {
        key: "avg_delay",
        title: t("teamPerformance.table.avgDelay"),
        align: "right",
        ...sorted("avg_delay"),
        render: (_, row) => formatDays(row.summary.avg_delay_days)
      },
      {
        key: "financial",
        title: t("teamPerformance.table.financial"),
        align: "right",
        ...sorted("financial"),
        render: (_, row) =>
          drillCell(
            row,
            "financial_exceptions",
            scoreCell(row.financial_score, "financial")
          )
      },
      {
        key: "over_budget",
        title: t("teamPerformance.table.overBudget"),
        align: "right",
        render: (_, row) => row.summary.overrun_projects
      },
      {
        key: "revenue_gap",
        title: t("teamPerformance.table.revenueGap"),
        align: "right",
        render: (_, row) =>
          amountsVisible ? formatMoney(row.summary.gap_amount) : "—"
      },
      {
        key: "status",
        title: t("teamPerformance.table.status"),
        render: (_, row) => (
          <StatusTag status={row.status.value} reasons={row.status.reasons} />
        )
      }
    ],
    [t, amountsVisible, activeSort, onOpenMember]
  )

  const handleChange = (
    _pagination: unknown,
    _filters: unknown,
    sorter:
      | SorterResult<PerformanceMemberRow>
      | SorterResult<PerformanceMemberRow>[]
  ) => {
    const single = Array.isArray(sorter) ? sorter[0] : sorter
    const next =
      single?.order && single.columnKey
        ? { key: String(single.columnKey), dir: single.order }
        : null
    if (onSortChange) onSortChange(next)
    else setLocalSort(next ?? undefined)
  }

  return (
    <Table<PerformanceMemberRow>
      rowKey="id"
      size="middle"
      columns={columns}
      dataSource={sortRows(rows, activeSort)}
      onChange={handleChange}
      scroll={{ x: 1100 }}
      showSorterTooltip={false}
      pagination={
        rows.length > PAGE_SIZE
          ? { pageSize: PAGE_SIZE, showSizeChanger: false }
          : false
      }
    />
  )
}
